import { useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const MODES = {
  rescue: {
    label: "Import Rescue Cases",
    endpoint: "/import/csv",
    description:
      "Upload your Google Sheet export with columns: Sno., Date of Admission, Name, Case History, Breed, Color, Donation Needed, Animal Picture Link, Current Status, Last Status, Status Change Date, Status Change Image Url, Case URL.",
  },
  sanctuary: {
    label: "Import Sanctuary Residents",
    endpoint: "/import/csv-sanctuary",
    description:
      "Upload your sanctuary roster with columns: Name, Animal Type, Breed, Color, Description, Arrival Date (DD/MM/YYYY, optional), Monthly Care Cost (optional), Photo Link (optional). All rows are imported with status 'Sanctuary Resident'.",
  },
};

export default function CsvImport() {
  const [mode, setMode] = useState("rescue");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);
  const { toast } = useToast();

  const handleModeChange = (m) => {
    setMode(m);
    setFile(null);
    setReport(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MODES[mode].endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      toast({ title: "Import complete", description: `${data.imported} cases imported.` });
    } catch (err) {
      toast({ variant: "destructive", title: "Import failed", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2">CSV Import</h1>

        <div className="flex gap-2 mb-4" data-testid="csv-import-mode-toggle">
          {Object.entries(MODES).map(([key, m]) => (
            <button
              key={key}
              onClick={() => handleModeChange(key)}
              data-testid={`csv-import-mode-${key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                mode === key ? "bg-amtm-teal text-amtm-text" : "bg-amtm-surface border border-amtm-border text-amtm-textMuted"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <p className="text-amtm-textMuted mb-6">{MODES[mode].description}</p>

        <div className="bg-amtm-surface border-2 border-dashed border-amtm-teal/40 rounded-2xl p-8 flex flex-col items-center gap-3 mb-6">
          <UploadCloud className="h-10 w-10 text-amtm-teal" />
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="csv-file-input"
            className="text-sm"
          />
          <Button onClick={handleImport} disabled={!file || importing} data-testid="csv-import-button" className="mt-2 bg-amtm-teal text-white hover:bg-amtm-tealHover">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : MODES[mode].label}
          </Button>
        </div>

        {report && (
          <div className="space-y-4" data-testid="csv-import-report">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-amtm-success/10 border border-amtm-success/30 rounded-xl p-4 flex items-center gap-3" data-testid="import-imported-count">
                <CheckCircle2 className="h-6 w-6 text-amtm-success" />
                <div>
                  <p className="text-xl font-bold text-amtm-text">{report.imported}</p>
                  <p className="text-xs text-amtm-textMuted">Imported</p>
                </div>
              </div>
              <div className="bg-amtm-warning/10 border border-amtm-warning/30 rounded-xl p-4 flex items-center gap-3" data-testid="import-flagged-count">
                <AlertTriangle className="h-6 w-6 text-amtm-warning" />
                <div>
                  <p className="text-xl font-bold text-amtm-text">{report.flagged}</p>
                  <p className="text-xs text-amtm-textMuted">Flagged for Review</p>
                </div>
              </div>
            </div>

            {report.flagged_rows?.length > 0 && (
              <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="flagged-rows-list">
                {report.flagged_rows.map((row) => (
                  <div key={row.row_number} className="p-4">
                    <Link to={`/cases/${row.case_id}`} data-testid={`flagged-row-link-${row.row_number}`} className="font-semibold text-amtm-teal hover:underline">
                      Row {row.row_number} — Case #{row.case_number} — {row.animal_name}
                    </Link>
                    <ul className="text-sm text-amtm-textMuted list-disc pl-5 mt-1">
                      {row.issues.map((issue) => (
                        <li key={issue}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
